import { api, BASE_URL, getToken } from './client';

export interface WorkspaceInfo {
  id: string;
  name: string;
  filePath: string;
  sourceLegacyFileHash: string | null;
  createdAt: string;
  lastOpenedAt: string;
  isActive: boolean;
  companyName: string | null;
  companyCode: string | null;
  /** When true, AI data egress is disabled (no data sent to external AI providers) */
  aiEgressDisabled: boolean;
  /** "mysql" = synced from a hosted cloud database (Phase D); null/other = uploaded file */
  sourceKind: string | null;
  sourceDatabase: string | null;
  lastSyncedAtUtc: string | null;
  /** Owner of the hosted DB (shop account email) — login-independent, admin list only. */
  ownerEmail?: string | null;
  /** Soft-removed (Ištrinti hides; Atkurti brings back) — admin list only. */
  hidden?: boolean;
  /** ADR-010/012: write-back instance slug when configured (mirror workspaces only). */
  writebackSlug?: string | null;
  /** ADR-010/012: write-back credentials configured — drafts can be promoted
   *  into the client's Apskaita5 book (computed server-side). */
  writebackConfigured?: boolean;
}

export interface SetWritebackRequest {
  slug: string;
  appName: string;
  token: string;
}

export interface CreateWorkspaceRequest {
  name: string;
  companyName?: string;
  companyCode?: string;
}

export interface UpdateWorkspaceRequest {
  name?: string;
  companyName?: string;
  companyCode?: string;
  /** Set true to disable AI data egress for this workspace */
  aiEgressDisabled?: boolean;
}

const WORKSPACE_ID_KEY = 'a5core_workspace_id';

export function getWorkspaceId(): string | null {
  return localStorage.getItem(WORKSPACE_ID_KEY);
}

export function setWorkspaceId(id: string): void {
  localStorage.setItem(WORKSPACE_ID_KEY, id);
}

export function clearWorkspaceId(): void {
  localStorage.removeItem(WORKSPACE_ID_KEY);
}

export const workspacesApi = {
  // /workspaces is AdminOnly and returns EVERYTHING (the admin management
  // page); /workspaces/mine returns the current user's OWN + shared
  // memberships for every role (admins included — the daily switcher shows
  // no more than what is shared with them).
  list: () =>
    api.get<WorkspaceInfo[]>('/workspaces/mine'),

  /** Admin-only: every workspace on the server (management surface). */
  listAll: () =>
    api.get<WorkspaceInfo[]>('/workspaces'),

  /** Admin-only: every workspace + its members (kas klientas), hidden included. */
  listAllWithMembers: () =>
    api.get<{ workspace: WorkspaceInfo; members: string[] }[]>('/workspaces/with-members'),

  /** Admin: sweep the MySQL catalog (create missing workspaces) + sync all. */
  syncAll: () =>
    api.post<{ workspacesCreated: number; synced: number; failures: number }>('/workspaces/sync-all'),

  /** Restore a soft-removed workspace (Atkurti). */
  restore: (id: string) =>
    api.post<WorkspaceInfo>(`/workspaces/${id}/restore`),

  get: (id: string) =>
    api.get<WorkspaceInfo>(`/workspaces/${id}`),

  create: (data: CreateWorkspaceRequest) =>
    api.post<WorkspaceInfo>('/workspaces', data),

  syncNow: (id: string) =>
    api.post<{ workspaceId: string; database: string; success: boolean; recordsImported: number; durationMs: number }>(`/workspaces/${id}/sync`),

  update: (id: string, data: UpdateWorkspaceRequest) =>
    api.put<WorkspaceInfo>(`/workspaces/${id}`, data),

  /** Admin: configure (or clear, with all-empty values) per-workspace write-back
   *  — the credential link that lets mirror-workspace drafts be registered in
   *  the client's Apskaita5 book (ADR-010/012). 204 on success. */
  setWriteback: (id: string, data: SetWritebackRequest) =>
    api.put<void>(`/admin/workspaces/${id}/writeback`, data),

  activate: (id: string) =>
    api.post<WorkspaceInfo>(`/workspaces/${id}/activate`),

  delete: (id: string) =>
    api.delete<void>(`/workspaces/${id}`),

  getActive: () =>
    api.get<WorkspaceInfo | null>('/workspaces/active'),

  exportUrl: (id: string) =>
    `${BASE_URL}/workspaces/${id}/export`,
};

/**
 * Build headers that include both JWT token and workspace ID.
 * Used for file download via fetch (since api.get doesn't support blob responses).
 */
export function workspaceFetchHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const workspaceId = getWorkspaceId();
  if (workspaceId) headers['X-Workspace-Id'] = workspaceId;
  return headers;
}
